import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppId, AppThunkAPIType } from "../../app/appTypes"
import { BlobResponse, deleteWithAuth, deleteWithAuthAndBody, getBlobWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../http"
import { DocumentSettings } from "../systemSettings/systemDocumentSettings/SystemDocumentSettingsApi"
import { DocSectionType, DocumentLanguages, DocumentTypes } from "./documentTypes"
import { ProjectProcessHeader } from "../project/projectProcess/ProjectProcessApiClient"
import { DocumentCreationMode, DocumentHeader } from "./documentsApi"
import { DateFromModeEnum, DateToModeEnum, IntervalUnitEnum } from "../common/commonTypes"

export interface UpdateQmsDateFromDto {
    mode: DateFromModeEnum
    effectiveFrom?: string
}

export interface UpdateQmsDateToDto {
    mode: DateToModeEnum
    effectiveTo?: string
    validityIntervalValue?: number
    validityIntervalUnit?: IntervalUnitEnum
}

export const updateQmsDateFrom = createAsyncThunk<
    DocumentHeader,
    { documentId: AppId; dto: UpdateQmsDateFromDto },
    AppThunkAPIType
>(
    "document/qms/update-date-from",
    async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/qms-documents/${documentId}/qms/date-from`,
            payload: dto,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const updateQmsDateTo = createAsyncThunk<
    DocumentHeader,
    { documentId: AppId; dto: UpdateQmsDateToDto },
    AppThunkAPIType
>(
    "document/qms/update-date-to",
    async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/qms-documents/${documentId}/qms/date-to`,
            payload: dto,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export interface UpdateQmsReviewDto {
    requiresReview: boolean
    reviewIntervalValue?: number
    reviewIntervalUnit?: IntervalUnitEnum
    reviewDueDate?: string | null
}

export interface DocumentDownloadUserStatsDto {
    userId: string
    userName: string
    downloadCount: number
    lastDownloadDate?: string | null
}

export interface DocumentDownloadStatsDto {
    documentId: number
    totalDownloadCount: number
    uniqueUsersCount: number
    users: DocumentDownloadUserStatsDto[]
}

export const updateQmsReview = createAsyncThunk<
    DocumentHeader,
    { documentId: AppId; dto: UpdateQmsReviewDto },
    AppThunkAPIType
>(
    "document/qms/update-review",
    async ({ documentId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/qms-documents/${documentId}/qms/review`,
            payload: dto,
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadDocumentDownloadStats = createAsyncThunk<
    DocumentDownloadStatsDto,
    { documentId: AppId },
    AppThunkAPIType
>(
    "document/qms/download-stats",
    async ({ documentId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/documents-statistics/${documentId}/downloads/stats`,
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentDownloadStatsDto
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export interface CreateQmsDocumentDto {
    documentType: string
    documentNumber: string
    title: string
    templateId: number
    language: string
    creationMode: DocumentCreationMode
    structureNodeId?: string
}

export interface StructureNodeDocumentDto {
    documentId: number
    documentNumber: string
    title: string
    documentType: string
    status?: string
    linkedNodeId: string
    linkedNodeName: string
    isInheritedFromParent: boolean
    order: number
}

export const loadAllQMSDocuments = createAsyncThunk<DocumentHeader[], void, AppThunkAPIType>("/qms-documents/load",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/qms-documents` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadDocumentsForStructureNode = createAsyncThunk<
    StructureNodeDocumentDto[],
    { nodeId: string; includeParents?: boolean },
    AppThunkAPIType
>(
    "/qms-documents/load-by-structure-node",
    async ({ nodeId, includeParents = false }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/qms-documents/structure-nodes/${nodeId}/documents?includeParents=${includeParents}`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as StructureNodeDocumentDto[]
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const loadTemplatesForStructureNode = createAsyncThunk<
    StructureNodeDocumentDto[],
    { nodeId: string; includeParents?: boolean },
    AppThunkAPIType
>(
    "/qms-documents/load-templates-by-structure-node",
    async ({ nodeId, includeParents = false }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/qms-documents/structure-nodes/${nodeId}/templates?includeParents=${includeParents}`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as StructureNodeDocumentDto[]
        }

        return rejectWithValue(payload ?? { kind: "unknown" })
    }
)

export const createQMSDocument = createAsyncThunk<DocumentHeader, CreateQmsDocumentDto, AppThunkAPIType>("/qms-documents/create",
    async (dto, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/qms-documents",
            payload: dto,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    })

export const createQMSTemplate = createAsyncThunk<DocumentHeader, CreateQmsDocumentDto, AppThunkAPIType>("/qms-documents/templates/create",
    async (dto, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/qms-documents/templates",
            payload: dto,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as DocumentHeader
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    })